import { useMemo } from 'react';
import { SourcesDailyStatsResponse } from '../../../../shared/services/monitor/data/sources/MonitorDataSourcesService';
import { useTranslation } from 'react-i18next';

export const useSourceUsageMatrix = (
  currentStats: SourcesDailyStatsResponse | undefined,
  previousStats: SourcesDailyStatsResponse | undefined,
  sourceUrl: string,
  showComparison: boolean = true
): {
  matrix: (string | number)[][] | null;
  headers: string[];
} => {
  const { t } = useTranslation();

  const currentLabel = t('currentPeriod');
  const previousLabel = t('previousPeriod');

  const result = useMemo(() => {
    if (!currentStats || Object.keys(currentStats).length === 0 || !sourceUrl) {
      return { matrix: null, headers: [] };
    }

    const currentDates = Object.keys(currentStats).sort(
      (a, b) => new Date(a).getTime() - new Date(b).getTime()
    );

    const hasPrevious = showComparison && previousStats && Object.keys(previousStats).length > 0;
    const previousDates = hasPrevious
      ? Object.keys(previousStats).sort((a, b) => new Date(a).getTime() - new Date(b).getTime())
      : [];

    const headers = hasPrevious ? [currentLabel, previousLabel] : [currentLabel];
    const header: (string | number)[] = hasPrevious
      ? ['date', currentLabel, previousLabel]
      : ['date', currentLabel];

    const matrix: (string | number)[][] = [header];

    currentDates.forEach((date, index) => {
      const currentEntry = currentStats[date]?.find((s) => s.source === sourceUrl);
      const currentCount = currentEntry?.count ?? 0;

      if (hasPrevious) {
        let prevCount = 0;
        if (index < previousDates.length) {
          const prevDate = previousDates[index];
          const prevEntry = previousStats[prevDate]?.find((s) => s.source === sourceUrl);
          prevCount = prevEntry?.count ?? 0;
        }
        matrix.push([date, currentCount, prevCount]);
      } else {
        matrix.push([date, currentCount]);
      }
    });

    return {
      matrix: matrix.length > 1 ? matrix : null,
      headers,
    };
  }, [currentStats, previousStats, sourceUrl, currentLabel, previousLabel, showComparison]);

  return result;
};
